// ===================================================
// src/components/Planning/hooks/usePlanningData.js
// WITH FORECAST, LEAD TIME, PROJECTED DATES, WAREHOUSE BREAKDOWN & DAILY INVENTORY FORECAST
// ===================================================
import { useState, useEffect, useCallback } from 'react';
import api from '../../../services/api';
import * as XLSX from 'xlsx';

export const usePlanningData = () => {
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    totalSales: 0,
    totalManufacturing: 0,
    totalStock: 0,
    latestStock: 0,
    averageDelta: 0,
    projectedUS30: 0,
    projectedUS60: 0,
    projectedEU30: 0,
    projectedEU60: 0,
    projectedDate30: null,
    projectedDate60: null,
    warehouseBreakdown: {},
    projectedDailyInventory: []
  });

  const toISODate = (dt) =>
    (dt instanceof Date ? dt : new Date(dt)).toISOString().split('T')[0];

  // 📊 Fetch all planning data
  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      console.log('📊 Fetching all planning data...');
      const response = await api.get('/plan/all');
      const days = response?.data?.days || [];
      console.log('✅ Planning data received:', days.length, 'days');

      // 🧩 Find last day that has total_stock > 0
      const lastDay = [...days].reverse().find(d => (d?.stock?.total_stock || 0) > 0);

      if (!lastDay) {
        console.warn('⚠️ No day with total_stock > 0 found in /plan/all');
        setAllData(days);
        calculateStats(days, null);
        return;
      }

      console.log('📦 Found last day with stock:', lastDay.date);

      // 🔍 Fetch detailed data for that last stock day
      const detailedRes = await api.get(`/plan/day/${lastDay.date}`);
      const detailedDay = detailedRes.data;
      console.log('📅 Detailed day loaded:', detailedDay.date);

      setAllData(days);
      calculateStats(days, detailedDay);

    } catch (err) {
      console.error('❌ Error fetching planning data:', err);
      setError(err.message || 'Failed to fetch planning data');
    } finally {
      setLoading(false);
    }
  }, []);

  // 🗑️ Delete planning day by date
  const handleDeleteDay = async (date) => {
    try {
      console.log('🗑️ Deleting planning day:', date);
      await api.delete(`/plan/day/${date}`);
      console.log(`✅ Deleted planning day ${date}`);
    } catch (error) {
      console.error('❌ Error deleting planning day:', error);
      throw error;
    }
  };

  // 📅 Fetch details for a specific planning day
const fetchDayDetails = async (date) => {
  try {
    console.log('📅 Fetching day details for:', date);
    const res = await api.get(`/plan/day/${date}`);
    console.log('✅ Detailed day received:', res.data);
    return res.data;
  } catch (error) {
    console.error('❌ Failed to fetch day details:', error);
    throw error;
  }
};

function generateDailyPlanUpdate(formData) {
  const { target_total, production_target_total, sales_percent, production_percent, ml_split } = formData;

  const fSales = {};
  const fProd = {};

  Object.keys(sales_percent).forEach(key => {
    const baseSales = target_total * (sales_percent[key] / 100);
    const baseProd = production_target_total * (production_percent[key] / 100);
    const split = ml_split[key] || { m: 50, l: 50 };

    fSales[`${key}_m`] = Math.round(baseSales * (split.m / 100));
    fSales[`${key}_l`] = Math.round(baseSales * (split.l / 100));
    fProd[`${key}_m`] = Math.round(baseProd * (split.m / 100));
    fProd[`${key}_l`] = Math.round(baseProd * (split.l / 100));
  });

  return {
    ...formData,
    forecast: {
      sales: fSales,
      production: fProd
    }
  };
}



  const handleUpdateDay = async (formData) => {
    try {
      const { actual_date } = formData;
      if (!actual_date) throw new Error("Missing actual_date");

      console.log("🧩 Updating day via API:", actual_date);

      // ✅ Update backend
      await api.put(`/plan/detailed`, formData);

      // 🧠 Store the last updated date for full re-simulation
      localStorage.setItem("planning_last_updated", actual_date);

      console.log("✅ Day updated successfully — recalculation will trigger on next render");
      await fetchAll(); // Refresh planning data
    } catch (error) {
      console.error("❌ Failed to update planning day:", error);
      throw error;
    }
  };


  // 🧮 Calculate statistics & projections
  const calculateStats = (days, detailedDay = null) => {
    if (!Array.isArray(days) || days.length === 0) {
      setStats({
        totalSales: 0,
        totalManufacturing: 0,
        totalStock: 0,
        latestStock: 0,
        averageDelta: 0,
        projectedUS30: 0,
        projectedUS60: 0,
        projectedEU30: 0,
        projectedEU60: 0,
        projectedDate30: null,
        projectedDate60: null,
        warehouseBreakdown: {},
        projectedDailyInventory: []
      });
      return;
    }

    // Find last valid stock day
    const lastDayWithStock = [...days].reverse().find(d => (d?.stock?.total_stock || 0) > 0);
    console.log('📅 Using last valid stock day:', lastDayWithStock?.date);

    // Totals
    const totalSales = days.reduce((sum, d) => sum + (d?.sales?.actual_total || 0), 0);
    const totalManufacturing = days.reduce((sum, d) => {
      if (typeof d?.manufacturing?.actual_total === 'number')
        return sum + d.manufacturing.actual_total;
      return (
        sum +
        (d?.manufacturing?.actual_us || 0) +
        (d?.manufacturing?.actual_eu || 0)
      );
    }, 0);

    const averageDelta =
      days.reduce((sum, d) => sum + (d?.sales?.accumulated_delta || 0), 0) /
      days.length;

    // Stock totals
    const latestStock = lastDayWithStock?.stock?.total_stock || 0;
    const totalStock = days.reduce((sum, d) => {
      const s = d?.stock || {};
      return (
        sum + (s.us_itb || 0) + (s.eu_tive || 0) + (s.us_amazon || 0) + (s.eu_amazon || 0)
      );
    }, 0);

    // Current region stocks
    const s = lastDayWithStock?.stock || {};
    const currentStockUS = (s.us_itb || 0) + (s.us_amazon || 0);
    const currentStockEU = (s.eu_tive || 0) + (s.eu_amazon || 0);

    // Averages
    const avgSalesUS =
      days.reduce((acc, d) => acc + (d?.sales?.actual_us || 0), 0) / days.length;
    const avgSalesEU =
      days.reduce((acc, d) => {
        const eu = d?.sales?.actual_eu;
        const uk = d?.sales?.actual_uk;
        return acc + (typeof eu === 'number' ? eu : uk || 0);
      }, 0) / days.length;

    const avgMFGUS =
      days.reduce((acc, d) => acc + (d?.manufacturing?.actual_us || 0), 0) /
      days.length;
    const avgMFGEU =
      days.reduce((acc, d) => acc + (d?.manufacturing?.actual_eu || 0), 0) /
      days.length;

    // Lead time averages
    const avgLeadTimeUS =
      days.reduce((acc, d) => {
        const dd = d?.shipment?.duration_days?.china_us;
        if (typeof dd === 'number') return acc + dd;
        const worst = d?.shipment?.us?.worst_delivery_delay;
        return acc + (typeof worst === 'number' ? worst : 0);
      }, 0) / days.length || 0;

    const avgLeadTimeEU =
      days.reduce((acc, d) => {
        const dd = d?.shipment?.duration_days?.china_uk;
        if (typeof dd === 'number') return acc + dd;
        const worst =
          d?.shipment?.uk?.worst_delivery_delay ||
          d?.shipment?.eu?.worst_delivery_delay;
        return acc + (typeof worst === 'number' ? worst : 0);
      }, 0) / days.length || 0;

    // Forecast formula (existing projections)
    const calcProjected = (current, avgMFG, avgSales, avgLeadTime, daysAhead) => {
      const effectiveDays = Math.max(0, daysAhead - avgLeadTime);
      return Math.round(current + (avgMFG - avgSales) * effectiveDays);
    };

    const projectedUS30 = calcProjected(
      currentStockUS,
      avgMFGUS,
      avgSalesUS,
      avgLeadTimeUS,
      30
    );
    const projectedUS60 = calcProjected(
      currentStockUS,
      avgMFGUS,
      avgSalesUS,
      avgLeadTimeUS,
      60
    );
    const projectedEU30 = calcProjected(
      currentStockEU,
      avgMFGEU,
      avgSalesEU,
      avgLeadTimeEU,
      30
    );
    const projectedEU60 = calcProjected(
      currentStockEU,
      avgMFGEU,
      avgSalesEU,
      avgLeadTimeEU,
      60
    );

    // Future projection dates
    const base = lastDayWithStock?.date ? new Date(lastDayWithStock.date) : new Date();
    const projectedDate30 = new Date(base);
    projectedDate30.setDate(base.getDate() + 30);
    const projectedDate60 = new Date(base);
    projectedDate60.setDate(base.getDate() + 60);

    // ✅ Daily Inventory Projection (Forecast)
    let projectedInventory = latestStock;
    const futureInventory = [];

    const sortedDays = [...days].sort((a, b) => new Date(a.date) - new Date(b.date));
    for (const d of sortedDays) {
      const dayDate = new Date(d.date);
      const baseDate = new Date(lastDayWithStock.date);
      if (dayDate <= baseDate) continue;

      const forecastSales =
        (d.forecast?.sales?.us_cityplay || 0) +
        (d.forecast?.sales?.us_20 || 0) +
        (d.forecast?.sales?.eu_cityplay || 0) +
        (d.forecast?.sales?.eu_20 || 0);

      const forecastProd =
        (d.forecast?.production?.us_cityplay || 0) +
        (d.forecast?.production?.us_20 || 0) +
        (d.forecast?.production?.eu_cityplay || 0) +
        (d.forecast?.production?.eu_20 || 0);

      projectedInventory = projectedInventory + forecastProd - forecastSales;

      futureInventory.push({
        date: d.date,
        forecast_sales: forecastSales,
        forecast_production: forecastProd,
        projected_stock: Math.max(0, projectedInventory)
      });
    }

    // === 🏭 Warehouse Breakdown (from detailedDay API) ===
    const warehouseBreakdown = {
      US: {
        ITB: {
          cityplay: {
            M: detailedDay?.detailed?.stock_us_cityplay_m || 0,
            L: detailedDay?.detailed?.stock_us_cityplay_l || 0
          },
          two_point_0: {
            M: detailedDay?.detailed?.stock_us_20_m || 0,
            L: detailedDay?.detailed?.stock_us_20_l || 0
          }
        },
        Amazon: {
          cityplay: {
            M: detailedDay?.detailed?.stock_amazon_us_cityplay_m || 0,
            L: detailedDay?.detailed?.stock_amazon_us_cityplay_l || 0
          },
          two_point_0: {
            M: detailedDay?.detailed?.stock_amazon_us_20_m || 0,
            L: detailedDay?.detailed?.stock_amazon_us_20_l || 0
          },
          total:
            (detailedDay?.detailed?.stock_amazon_us_cityplay_m || 0) +
            (detailedDay?.detailed?.stock_amazon_us_cityplay_l || 0) +
            (detailedDay?.detailed?.stock_amazon_us_20_m || 0) +
            (detailedDay?.detailed?.stock_amazon_us_20_l || 0)
        }
      },

      EU: {
        Tive: {
          cityplay: {
            M: detailedDay?.detailed?.stock_uk_cityplay_m || 0,
            L: detailedDay?.detailed?.stock_uk_cityplay_l || 0
          },
          two_point_0: {
            M: detailedDay?.detailed?.stock_uk_20_m || 0,
            L: detailedDay?.detailed?.stock_uk_20_l || 0
          }
        },
        Amazon: {
          cityplay: {
            M: detailedDay?.detailed?.stock_amazon_uk_cityplay_m || 0,
            L: detailedDay?.detailed?.stock_amazon_uk_cityplay_l || 0
          },
          two_point_0: {
            M: detailedDay?.detailed?.stock_amazon_uk_20_m || 0,
            L: detailedDay?.detailed?.stock_amazon_uk_20_l || 0
          },
          total:
            (detailedDay?.detailed?.stock_amazon_uk_cityplay_m || 0) +
            (detailedDay?.detailed?.stock_amazon_uk_cityplay_l || 0) +
            (detailedDay?.detailed?.stock_amazon_uk_20_m || 0) +
            (detailedDay?.detailed?.stock_amazon_uk_20_l || 0)
        }
      }

    };


    // ✅ Update Stats
    const nextStats = {
      totalSales: Math.round(totalSales),
      totalManufacturing: Math.round(totalManufacturing),
      totalStock: Math.round(totalStock),
      latestStock: Math.round(latestStock),
      averageDelta: Math.round(averageDelta),
      projectedUS30,
      projectedUS60,
      projectedEU30,
      projectedEU60,
      projectedDate30: toISODate(projectedDate30),
      projectedDate60: toISODate(projectedDate60),
      warehouseBreakdown,
      projectedDailyInventory: futureInventory
    };

    setStats(nextStats);
    console.log('📦 Daily inventory projection calculated:', futureInventory.length, 'days');
  };

    // ===================================================
  // 📤 Export all planning data to Excel
  // ===================================================
const handleExportData = () => {
  try {
    console.log("📤 Generating Excel file directly from allData...");

    if (!allData || allData.length === 0) {
      alert("No data to export");
      return;
    }

    const rows = allData.map((d) => ({
      Date: d.date,
      "Target Total": d.sales?.target_total || 0,
      "Actual Sales": d.sales?.actual_total || 0,
      "Manufacturing Total": d.manufacturing?.plan_target_total || 0,
      "Stock Total": d.stock?.total_stock || 0,
      "Amazon Stock":
        (d.stock?.amazon_us || 0) + (d.stock?.amazon_eu || 0),
      "China → US Days": d.shipment?.duration_days?.china_us || 0,
      "China → EU Days": d.shipment?.duration_days?.china_uk || 0,
      "US → Customer": d.shipment?.duration_days?.us_to_customer || 0,
      "EU → Customer": d.shipment?.duration_days?.eu_to_customer || 0,
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Planning");

    XLSX.writeFile(wb, "planning_export.xlsx");
    console.log("✅ Excel export complete");
  } catch (err) {
    console.error("❌ Export failed:", err);
    alert("Failed to export Excel");
  }
};


  // Initial load
  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  return {
    allData,
    loading,
    error,
    stats,
    handleDeleteDay,
    fetchDayDetails,
    handleUpdateDay,
    fetchAll,
    handleExportData
  };
};

export default usePlanningData;
